import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Hook que persiste el valor de una pestaña (u otro estado simple) en el query string
 * de la URL. Al refrescar la pantalla o compartir el link, el usuario vuelve al mismo lugar.
 *
 * Uso:
 *   const [tab, setTab] = usePersistentTab('tab', 'plan');
 *   <Tabs value={tab} onValueChange={setTab}>...
 *
 * El parámetro `paramKey` debe ser único dentro de cada página.
 */
export function usePersistentTab(paramKey, defaultValue = '') {
  const [searchParams, setSearchParams] = useSearchParams();
  // Estado local: source of truth para el componente
  const [value, setLocalValue] = useState(() => searchParams.get(paramKey) || defaultValue);

  // Si el query param cambia desde fuera (back/forward del navegador, link directo) sincronizamos
  useEffect(() => {
    const fromUrl = searchParams.get(paramKey) || defaultValue;
    if (fromUrl !== value) setLocalValue(fromUrl);
    // eslint-disable-next-line
  }, [searchParams.get(paramKey)]);

  const setValue = useCallback((next) => {
    setLocalValue(next);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (next === undefined || next === null || next === '') {
        p.delete(paramKey);
      } else {
        p.set(paramKey, next);
      }
      return p;
    }, { replace: true });
  }, [paramKey, setSearchParams]);

  return [value, setValue];
}
